import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import dynamic from 'next/dynamic'
import Link from 'next/link'
import { twMerge } from 'tailwind-merge'

import { CommercialOnly } from '@/components/common/CommercialOnly'
import LogoIcon from '@/components/common/LogoIcon'
import { SidebarNavItem } from '@/components/common/Sidebar/CollapsibleSidebar/shared'
import {
  useNavigationOptions,
  useSidebar,
} from '@/components/common/Sidebar/hooks'
import { isCommercialNavigationOption } from '@/components/common/Sidebar/lib'
import UserMenu from '@/components/common/Sidebar/UserMenu'
import { CuiSidebarStatus } from '@/components/organizations/Cui'

import { IS_FEDRAMP_ENVIRONMENT } from '@/constants/env'

import { useBillingOrganization } from '@/hooks/billing/useBillingOrganization'
import { useEbuyUnreadCount } from '@/hooks/ebuy/useEbuyUnreadCount'
import { useEntitlementCheck } from '@/hooks/entitlements'
import { useCanAccessMonitors } from '@/hooks/monitors/useCanAccessMonitors'
import { useHasMonitorUnread } from '@/hooks/monitors/useMonitors'
import { useActiveOrg } from '@/hooks/organization/useActiveOrg'
import { useActiveOrgId } from '@/hooks/organization/useActiveOrgId'

import {
  useSidebarActiveFlyoutId,
  useSidebarClearHoverSuppression,
  useSidebarCloseFlyout,
  useSidebarDelayedFlyoutId,
  useSidebarFlyoutVisible,
  useSidebarHoverSuppressed,
  useSidebarSetActiveFlyoutId,
  useSidebarSetDelayedFlyoutId,
  useSidebarSetFlyoutVisible,
} from '@/stores/SidebarStore'

import {
  NAVIGATION_ID_TO_TITLE,
  type NavigationId,
} from '@/types/navigation/Sidebar'

const ClientFlyoutPanel = dynamic(
  () =>
    import('@/components/common/Sidebar/CollapsibleSidebar/FlyoutPanel').then(
      (m) => m.default ?? m.FlyoutPanel,
    ),
  { ssr: false, loading: () => null },
)
const ClientHomeFlyoutContent = dynamic(
  () =>
    import('@/components/common/Sidebar/CollapsibleSidebar/HomeFlyout').then(
      (m) => m.HomeFlyoutContent,
    ),
  { ssr: false, loading: () => null },
)
const ClientSearchFlyoutContent = dynamic(
  () =>
    import('@/components/common/Sidebar/CollapsibleSidebar/SearchFlyout').then(
      (m) => m.SearchFlyoutContent,
    ),
  { ssr: false, loading: () => null },
)
const ClientPursuitsFlyoutContent = dynamic(
  () =>
    import('@/components/common/Sidebar/CollapsibleSidebar/PursuitsFlyout').then(
      (m) => m.PursuitsFlyoutContent,
    ),
  { ssr: false, loading: () => null },
)
const ClientLibraryFlyoutContent = dynamic(
  () =>
    import('@/components/common/Sidebar/CollapsibleSidebar/LibraryFlyout').then(
      (m) => m.LibraryFlyoutContent,
    ),
  { ssr: false, loading: () => null },
)
const ClientAskAiFlyoutContent = dynamic(
  () =>
    import('@/components/common/Sidebar/CollapsibleSidebar/AskAiFlyout').then(
      (m) => m.AskAiFlyoutContent,
    ),
  { ssr: false, loading: () => null },
)

export const SIDEBAR_WIDTH = 64

// Delay before flyout opens (allows quick exits to cancel)
const FLYOUT_OPEN_DELAY_MS = 175

// Delay before flyout closes (allows moving to flyout)
const FLYOUT_CLOSE_DELAY_MS = 25

// Delay before flyout closes animation (allows moving to flyout)
const FLYOUT_CLOSE_ON_LINK_CLICK_DELAY_MS = 75

// Flyout panel animation duration
const FLYOUT_ANIMATION_DURATION_MS = 300

const preloadFlyoutChunks = () => {
  const preloads: Array<Promise<unknown>> = [
    import('@/components/common/Sidebar/CollapsibleSidebar/FlyoutPanel'),
    import('@/components/common/Sidebar/CollapsibleSidebar/HomeFlyout'),
    import('@/components/common/Sidebar/CollapsibleSidebar/PursuitsFlyout'),
    import('@/components/common/Sidebar/CollapsibleSidebar/LibraryFlyout'),
    import('@/components/common/Sidebar/CollapsibleSidebar/AskAiFlyout'),
  ]
  if (!IS_FEDRAMP_ENVIRONMENT) {
    preloads.push(
      import('@/components/common/Sidebar/CollapsibleSidebar/SearchFlyout'),
    )
  }

  void Promise.all(preloads).catch(() => {
    // Ignore preload failures; dynamic imports retry on demand.
  })
}

export const CollapsibleSidebar: React.FC = () => {
  const { navigationOptions, settingsOption } = useNavigationOptions()
  const { data: billingOrganization } = useBillingOrganization()
  const { data: hasCuiEntitlement } = useEntitlementCheck({
    entitlement: 'cui_compliance',
    enabled: !IS_FEDRAMP_ENVIRONMENT,
  })
  const { activeOrg } = useActiveOrg()
  const { activeOrgId } = useActiveOrgId()
  const { userCanAccess } = useSidebar()

  // Drive the Inbox item's unread dot. The Inbox is the universal inbox, so the
  // dot reflects ANY source — monitors OR GSA eBuy. Each query is gated by its
  // own access/entitlement (empty orgId disables it) so non-entitled orgs don't
  // fetch.
  const { canAccessMonitors } = useCanAccessMonitors()
  const { data: hasMonitorUnread } = useHasMonitorUnread(
    canAccessMonitors ? activeOrgId : '',
  )
  const { data: hasEbuy } = useEntitlementCheck({ entitlement: 'ebuy' })
  const { data: ebuyUnread } = useEbuyUnreadCount({
    orgId: activeOrgId,
    enabled: !IS_FEDRAMP_ENVIRONMENT && hasEbuy === true,
  })
  const hasInboxUnread = (hasMonitorUnread ?? false) || (ebuyUnread ?? 0) > 0

  const activeFlyoutId = useSidebarActiveFlyoutId()
  const flyoutVisible = useSidebarFlyoutVisible()
  const hoverSuppressed = useSidebarHoverSuppressed()
  const setActiveFlyoutId = useSidebarSetActiveFlyoutId()
  const setFlyoutVisible = useSidebarSetFlyoutVisible()
  const closeFlyout = useSidebarCloseFlyout()
  const clearHoverSuppression = useSidebarClearHoverSuppression()

  const openTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  // Track pending option to show (before delay fires)
  const pendingOptionRef = useRef<string | null>(null)

  const delayedFlyoutId = useSidebarDelayedFlyoutId()
  const setDelayedFlyoutId = useSidebarSetDelayedFlyoutId()

  const flyoutCanAccess = useMemo(() => {
    if (activeFlyoutId === 'home') {
      return true
    }
    const activeOption = navigationOptions.find(
      (opt) => opt.id === activeFlyoutId,
    )
    return activeOption ? userCanAccess(activeOption) : true
  }, [activeFlyoutId, navigationOptions, userCanAccess])

  const clearAllTimeouts = useCallback(() => {
    if (openTimeoutRef.current) {
      clearTimeout(openTimeoutRef.current)
      openTimeoutRef.current = null
    }
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current)
      closeTimeoutRef.current = null
    }
  }, [])

  const handleItemHover = useCallback(
    (optionId: string | null) => {
      // If hover is suppressed (e.g., after navigation), ignore hover events
      if (hoverSuppressed) {
        return
      }

      // Cancel any pending close
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current)
        closeTimeoutRef.current = null
      }

      if (optionId) {
        pendingOptionRef.current = optionId

        // If flyout is already open, switch immediately
        if (flyoutVisible && activeFlyoutId !== null) {
          if (openTimeoutRef.current) {
            clearTimeout(openTimeoutRef.current)
            openTimeoutRef.current = null
          }
          setActiveFlyoutId(optionId)
        } else {
          // Otherwise, delay the open so quick exits can cancel
          if (openTimeoutRef.current) {
            clearTimeout(openTimeoutRef.current)
          }
          openTimeoutRef.current = setTimeout(() => {
            if (pendingOptionRef.current === optionId) {
              setActiveFlyoutId(optionId)
              setFlyoutVisible(true)
            }
            openTimeoutRef.current = null
          }, FLYOUT_OPEN_DELAY_MS)
        }
      } else {
        pendingOptionRef.current = null
      }
    },
    [
      activeFlyoutId,
      flyoutVisible,
      hoverSuppressed,
      setActiveFlyoutId,
      setFlyoutVisible,
    ],
  )

  const handleFlyoutMouseEnter = useCallback(() => {
    // Cancel any pending close when entering flyout
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current)
      closeTimeoutRef.current = null
    }
  }, [])

  const handleMouseLeave = useCallback(() => {
    // Cancel any pending open
    if (openTimeoutRef.current) {
      clearTimeout(openTimeoutRef.current)
      openTimeoutRef.current = null
    }
    pendingOptionRef.current = null

    // Clear hover suppression when mouse leaves the sidebar area
    clearHoverSuppression()

    // Delay the close to allow moving to flyout
    closeTimeoutRef.current = setTimeout(() => {
      closeFlyout()
      closeTimeoutRef.current = null
    }, FLYOUT_CLOSE_DELAY_MS)
  }, [clearHoverSuppression, closeFlyout])

  const handleCloseFlyoutOnLinkClick = useCallback(() => {
    setTimeout(() => {
      closeFlyout()
    }, FLYOUT_CLOSE_ON_LINK_CLICK_DELAY_MS)
  }, [closeFlyout])

  useEffect(() => {
    preloadFlyoutChunks()
    return () => {
      clearAllTimeouts()
    }
  }, [clearAllTimeouts])

  useEffect(() => {
    if (activeFlyoutId === null) {
      const setTimeoutId = setTimeout(() => {
        setDelayedFlyoutId(null)
      }, FLYOUT_ANIMATION_DURATION_MS)
      return () => {
        clearTimeout(setTimeoutId)
      }
    } else {
      setDelayedFlyoutId(activeFlyoutId)
    }
  }, [activeFlyoutId])

  const flyoutTitle =
    NAVIGATION_ID_TO_TITLE[delayedFlyoutId as NavigationId] ?? ''

  const flyoutIsVisible = flyoutVisible && activeFlyoutId !== null
  const showCuiStatus =
    !!activeOrgId && !!activeOrg && hasCuiEntitlement === true

  return (
    <div className="relative h-full w-full z-[50]">
      {/*
        something in the flyout panel is causing the sidebar
        to briefly flash when we navigate between routes
        */}
      <ClientFlyoutPanel
        title={flyoutTitle}
        // this still uses the activeFlyoutId to determine if the flyout is visible,
        // but the content itself is dependent on the delayedFlyoutId to make sure the
        // content doesn't disappear before the animation completes
        isVisible={flyoutIsVisible}
        canAccess={flyoutCanAccess}
        onMouseEnter={handleFlyoutMouseEnter}
        onMouseLeaveRight={handleMouseLeave}
        onClose={closeFlyout}
        animationDuration={FLYOUT_ANIMATION_DURATION_MS}
      >
        {delayedFlyoutId === 'home' && <ClientHomeFlyoutContent />}
        {delayedFlyoutId === 'search' && (
          <CommercialOnly>
            <ClientSearchFlyoutContent
              onNavigate={handleCloseFlyoutOnLinkClick}
            />
          </CommercialOnly>
        )}
        {delayedFlyoutId === 'pursuits' && <ClientPursuitsFlyoutContent />}
        {delayedFlyoutId === 'library' && <ClientLibraryFlyoutContent />}
        {delayedFlyoutId === 'ask-ai' && <ClientAskAiFlyoutContent />}
      </ClientFlyoutPanel>

      <div
        className={twMerge(
          'relative z-[2] flex flex-col h-full w-full',
          'bg-primary-50 dark:bg-default-dark',
          'border-r border-primary-300 dark:border-primary-800',
          // The rail is the dark half the professional theme needs so the warm
          // surfaces have something to be brighter than, and it is navy
          // rather than black because black next to cream reads as an
          // unstyled sidebar rather than a deliberate one.
          'professional:bg-accent-800 professional:border-accent-900',
        )}
        onMouseLeave={handleMouseLeave}
      >
        <div className="flex items-center justify-center h-16 flex-shrink-0">
          <Link
            href="/"
            className={twMerge(
              'group flex items-center justify-center w-11 h-11 rounded-lg',
              'transition-all duration-75 hover:scale-105',
            )}
          >
            {/* Two marks, one CSS pick, so the swap survives SSR without a
                mounted check: the warm theme paints the rail navy, where the
                pink mark muddies, so it gets the white one. */}
            <LogoIcon className="h-8 w-8 transition-transform duration-75 group-hover:scale-105 professional:hidden" />
            <LogoIcon
              variant="static-white"
              className="hidden h-8 w-8 transition-transform duration-75 group-hover:scale-105 professional:block"
            />
          </Link>
        </div>

        <nav className="flex-1 overflow-y-auto overflow-x-hidden w-full px-2">
          {/*
            we add a gap-px so that there is a brief moment when no items are hovered,
            allowing the hover suppression to reset after a page change;
            otherwise, there is no chance for the hover suppression to reset since
            we immedidately go from one icon/item to the next
            */}
          <ul className="flex flex-col items-center w-full gap-px">
            {navigationOptions.map((item) => {
              const hasAccess = userCanAccess(item)
              const isHovered = activeFlyoutId === item.id
              const navItem = (
                <li className="flex justify-center">
                  <SidebarNavItem
                    option={item}
                    canAccess={hasAccess}
                    isHovered={isHovered}
                    showDot={item.id === 'inbox' && hasInboxUnread}
                    onHover={handleItemHover}
                    onClick={handleCloseFlyoutOnLinkClick}
                  />
                </li>
              )

              return isCommercialNavigationOption(item) ? (
                <CommercialOnly key={item.id}>{navItem}</CommercialOnly>
              ) : (
                <React.Fragment key={item.id}>{navItem}</React.Fragment>
              )
            })}
          </ul>
        </nav>

        <div className="p-2">
          <SidebarNavItem
            option={settingsOption}
            canAccess={true}
            isHovered={activeFlyoutId === settingsOption.id}
            onHover={handleItemHover}
            onClick={handleCloseFlyoutOnLinkClick}
          />
        </div>

        <div className="mx-4 h-px bg-primary-200 dark:bg-primary-800 professional:bg-white/10" />

        <CommercialOnly>
          {showCuiStatus && (
            <div className="px-2 py-3">
              <CuiSidebarStatus orgId={activeOrgId} />
              <div className="mx-2 mt-2 h-px bg-primary-200 dark:bg-primary-800 professional:bg-white/10" />
            </div>
          )}
        </CommercialOnly>

        {billingOrganization !== null && billingOrganization !== undefined && (
          <div className="flex items-center justify-center w-full h-14">
            <UserMenu expanded={false} className="w-fit" />
          </div>
        )}
      </div>
    </div>
  )
}

export default CollapsibleSidebar
